import { supabase } from '@/lib/supabase';
import { PostgrestError } from '@supabase/supabase-js';

/**
 * Handles Supabase insertion errors, particularly unique constraint violations ('23505').
 * If a batch insert fails with a unique constraint error, it attempts to insert items individually.
 *
 * @param error The PostgrestError from the initial insert attempt.
 * @param items The array of items that were attempted to be inserted.
 * @param tableName The name of the table being inserted into.
 * @returns An object containing the counts of successful and failed individual insertions.
 */
export async function handleSupabaseUniqueConstraintError<T extends Record<string, any>>(
  error: PostgrestError,
  items: T[],
  tableName: string
): Promise<{ successCount: number; errorCount: number }> {
  let successCount = 0;
  let errorCount = 0;

  if (error.code === '23505') { // Unique constraint violation
    console.warn(`Batch insert failed due to unique constraint on table '${tableName}'. Attempting individual inserts.`);
    for (const item of items) {
      try {
        // Ensure 'created_by' and 'updated_by' are set if they might be missing
        // Use a type assertion to allow adding properties if they don't exist
        const itemToInsert: Record<string, any> = { ...item };
        if (!itemToInsert.created_by) {
          itemToInsert.created_by = 'system_fallback';
        }
        if (!itemToInsert.updated_by) {
          itemToInsert.updated_by = 'system_fallback';
        }

        const { error: singleInsertError } = await supabase
          .from(tableName)
          .insert([itemToInsert]); // Insert as an array

        if (singleInsertError) {
          // If even individual insert fails with unique constraint, just count as error
          if (singleInsertError.code === '23505') {
             errorCount++;
             console.warn(`Individual insert failed for item (already exists):`, item);
          } else {
             errorCount++;
             console.error(`Error inserting individual item into '${tableName}':`, singleInsertError, item);
          }
        } else {
          successCount++;
        }
      } catch (singleErr) {
        errorCount++;
        console.error(`Exception during individual insert into '${tableName}':`, singleErr, item);
      }
    }
  } else {
    // For other types of errors, count the entire batch as failed
    errorCount = items.length;
    console.error(`Batch insert failed for table '${tableName}' with error:`, error);
    // Optionally re-throw the original error if needed
    // throw error;
  }

  return { successCount, errorCount };
}


type CheckUniquenessParams = {
  tableName: string;
  columnName: string;
  value: string;
};

/**
 * Checks if a specific value already exists in a given table column.
 *
 * @param params Object containing tableName, columnName, and the value to check.
 * @returns True if the value is unique, false otherwise.
 */
async function checkUniqueness({ tableName, columnName, value }: CheckUniquenessParams): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from(tableName)
      .select('id') // Select a minimal column just to check existence
      .eq(columnName, value)
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error(`Error checking uniqueness in '${tableName}.${columnName}' for value '${value}':`, error);
      // Assume not unique to be safe if the check fails
      return false;
    }

    return !data; // If data is null/undefined, it's unique
  } catch (err) {
      console.error(`Exception during uniqueness check in '${tableName}.${columnName}' for value '${value}':`, err);
      return false; // Assume not unique on exception
  }
}


type GenerateUniqueCodeParams = {
  tableName: string;
  columnName: string;
  generateCodeFunction: () => string; // Function that generates a potential code
  maxAttempts?: number;
  prefix?: string;
  fallbackGenerator?: () => string; // Optional fallback if maxAttempts is reached
};

/**
 * Generates a unique code/ID for a specific table column by repeatedly calling a generation function
 * until a unique value is found or max attempts are reached.
 *
 * @param params Configuration object for uniqueness generation.
 * @returns The generated unique code.
 * @throws Error if a unique code cannot be generated within maxAttempts.
 */
export async function generateUniqueCode({
  tableName,
  columnName,
  generateCodeFunction,
  maxAttempts = 10,
  prefix = '',
  fallbackGenerator
}: GenerateUniqueCodeParams): Promise<string> {
  let isUnique = false;
  let uniqueCode = '';
  let attempts = 0;

  while (!isUnique && attempts < maxAttempts) {
    attempts++;
    const generatedValue = generateCodeFunction();
    uniqueCode = `${prefix}${generatedValue}`;

    isUnique = await checkUniqueness({ tableName, columnName, value: uniqueCode });
  }

  if (!isUnique) {
      if (fallbackGenerator) {
          console.warn(`Could not generate unique code for ${tableName}.${columnName} after ${maxAttempts} attempts using primary generator. Using fallback.`);
          uniqueCode = fallbackGenerator();
          // Optionally, perform one last check with the fallback code
          isUnique = await checkUniqueness({ tableName, columnName, value: uniqueCode });
          if (!isUnique) {
               console.error(`Fallback generator also failed to produce a unique code for ${tableName}.${columnName}. Falling back to timestamp-based code.`);
               // Final fallback to a high-entropy timestamp code
                uniqueCode = `${prefix}ERR_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
               // Assuming this final fallback IS unique enough, though not guaranteed
               // throw new Error(`Failed to generate a unique code for ${tableName}.${columnName} even with fallback.`);
          }
      } else {
          console.error(`Could not generate unique code for ${tableName}.${columnName} after ${maxAttempts} attempts. Falling back to timestamp-based code.`);
           // Final fallback to a high-entropy timestamp code
           uniqueCode = `${prefix}ERR_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
          // throw new Error(`Failed to generate a unique code for ${tableName}.${columnName} after ${maxAttempts} attempts.`);
      }
  }

  return uniqueCode;
}

// --- Specific Code Generators ---

/**
 * Generates a random 6-digit number string.
 */
export const generateRandom6DigitNumber = (): string => {
    return Math.floor(100000 + Math.random() * 900000).toString();
}

/**
 * Generates a random 3-digit number string.
 */
export const generateRandom3DigitNumber = (): string => {
    return Math.floor(100 + Math.random() * 900).toString();
}


/**
 * Generates a random 5-digit number string.
 */
export const generateRandom5DigitNumber = (): string => {
    return Math.floor(10000 + Math.random() * 90000).toString();
}

/**
 * Generates a random 10-digit number string.
 */
export const generateRandom10DigitNumber = (): string => {
    // Ensure it's exactly 10 digits by padding if needed (though random range makes it unlikely)
    return Math.floor(1000000000 + Math.random() * 9000000000).toString();
}

/**
 * Generates a display ID in the format 101479-473 using random numbers.
 */
export const generateSubscriptionDisplayId = (): string => {
    return `${generateRandom6DigitNumber()}-${generateRandom3DigitNumber()}`;
};

/**
 * Generates a course number in the format CRS-12345 using random numbers.
 */
export const generateCourseNumber = (): string => {
    // Reusing the 5-digit generator
    return generateRandom5DigitNumber();
}

/**
 * Generates a voucher code value (the part after prefix) using random numbers.
 */
export const generateVoucherCodeValue = (): string => {
    // Reusing the 10-digit generator
    return generateRandom10DigitNumber();
}

/**
 * Generates a fallback display ID using timestamp and random part.
 * Example: 123456-789 (last 6 digits of timestamp + 3 random digits)
 */
export const generateFallbackDisplayId = (): string => {
    const timestamp = Date.now().toString().slice(-6);
    const randomPart = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `${timestamp}-${randomPart}`;
}; 